import React, { useMemo, useCallback, useRef } from "react";
import styled from "styled-components";
import useComponentSize from "@rehooks/component-size";
import ReactGA from "react-ga";

import get from "lodash/get";
import at from "lodash/at";

import matchSorter from "match-sorter";
import { useSelector } from "react-redux";
import { Button, Cutout, Progress, Hourglass } from "react95";
import FormContainer from "./SearchForm/FormContainer";
import useSelectedScraper from "../hooks/useSelectedScraper";
import TrackTable from "./TrackTable/trackTable";

const LoadingContainer = styled.div`
    margin-top: 15px;
    display: flex;
    justify-content: center;
`;

const ScraperWindow = () => {
    const selectedScraper = useSelectedScraper();

    const [
        trackDetails,
        downloadUrl,
        fetchingBlob,
        blobProgress,
        artistTracks,
        filters,
        trackTitle,
        maxResults,
        artistSearch
    ] = useSelector((state) =>
        at(
            state,
            `trackDetails.${selectedScraper}.value`,
            `trackBlob.${selectedScraper}.url`,
            `trackBlob.${selectedScraper}.isLoading`,
            `trackBlob.${selectedScraper}.progress`,
            `artistTracks.${selectedScraper}`,
            `searchFilters.${selectedScraper}`,
            `trackName.${selectedScraper}.value`,
            `maxResults.${selectedScraper}.value`,
            `artistSearch.${selectedScraper}.isLoading`
        )
    );

    const trackData = get(artistTracks, "data", []);
    const fetchingArtistTracks = get(artistTracks, "isLoading", false);

    const showNumResults = (arr) => {
        if (!arr) return null;

        const numResults = maxResults === arr.length ? maxResults : arr.length;

        return (
            <>{`Showing ${numResults} ${
                numResults > 1 || numResults === 0 ? "results" : "result"
            } of ${artistTracks.data.length}`}</>
        );
    };

    const filterSize = (trackArray, key, condition, size) => {
        return trackArray.filter((track) => {
            switch (condition) {
                case "AT_LEAST":
                    return get(track, `${key}`) >= size;
                case "EQUAL_TO":
                    return get(track, `${key}`) === size;
                default:
                    return true;
            }
        });
    };

    const filterTrackData = useCallback(
        (unfilteredData) => {
            if (!unfilteredData) return null;
            let filteredData = filters.reduce(
                (accu, curr) => filterSize(accu, curr.key, curr.condition, curr.size),
                unfilteredData
            );

            filteredData = matchSorter(filteredData, trackTitle, {
                keys: ["title"]
            });

            return filteredData.slice(0, maxResults);
        },
        [filters, maxResults, trackTitle]
    );

    const filteredTrackData = useMemo(() => filterTrackData(trackData), [
        filterTrackData,
        trackData
    ]);

    const ref = useRef(null);
    const size = useComponentSize(ref);

    return (
        <>
            <FormContainer />

            {/* render track table */}
            {artistSearch && (
                <LoadingContainer>
                    <Hourglass />
                </LoadingContainer>
            )}

            {!fetchingArtistTracks && filteredTrackData.length > 0 && (
                <div>
                    <TrackTable data={filteredTrackData} />

                    <Cutout shadow={false} style={{ height: "40px", background: "#9d9d9b" }}>
                        <h1
                            style={{
                                textAlign: "center",
                                color: "black",
                                fontSize: "1rem",
                                marginTop: "0.5rem"
                            }}
                        >
                            {showNumResults(filteredTrackData)}
                        </h1>
                    </Cutout>

                    <div
                        ref={ref}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: 3
                        }}
                    >
                        {fetchingBlob ? (
                            <Progress
                                percent={blobProgress}
                                shadow={false}
                                width={size.width / 2}
                            />
                        ) : (
                            <Button
                                style={{ width: "50%", marginRight: 3 }}
                                disabled={!downloadUrl}
                            >
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={downloadUrl || ""}
                                    download={`${get(trackDetails, "title")}.mp3`}
                                    style={{ width: "100%" }}
                                    onClick={() => {
                                        ReactGA.event({
                                            category: "User",
                                            action: `Downloaded track ${get(trackDetails, "title")}`
                                        });
                                    }}
                                >
                                    {!downloadUrl ? `Select a track` : `Get selected track`}
                                </a>
                            </Button>
                        )}

                        <Button style={{ width: "50%" }} disabled={!get(trackDetails, "share")}>
                            <a
                                href={get(trackDetails, "share", "")}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ width: "100%" }}
                            >
                                View on {selectedScraper}
                            </a>
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ScraperWindow;
